import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
  WIDGET_REGISTRY,
  WidgetView,
  deriveAlt,
  adaptLegacyChart,
  coerceWidgetData,
  type WidgetData,
  type WidgetType,
} from '@docit/doc-widgets';

/**
 * The v2 lane's widget rendering + insertion (doc-widgets §D2/D6).
 *
 * Rendering: NodeView factories handed to editor-core through its `extraNodeViews` seam — the
 * engine stays React-free, the skin mounts the SAME `WidgetView` the v1 editor uses, so the two
 * lanes cannot drift (one registry, one component). `chart` nodes (the deployed legacy carrier)
 * are adapted `kind → type` here exactly as the v1 Chart node does.
 *
 * MOUNTING IS A HOST SEAM (slate-feature-transfer W6 §C4). `ReactDOM.render` /
 * `unmountComponentAtNode` were REMOVED in React 19 and are no longer even declared by
 * `@types/react-dom@19`, while knowit-main is still on React 17 — the oldest host sets this
 * package's peer floor. So the legacy pair stays the DEFAULT (React 17 keeps working with no
 * host change) and a React-19 host passes its own {@link WidgetRenderer} built on
 * `react-dom/client` (`./render19`, which only that host imports, so `react-dom/client` never
 * enters React 17's module graph). One package, two hosts, `WidgetView` unchanged.
 */

/**
 * How a host mounts a React element into a NodeView's DOM. `mount` is called again for the
 * same element on every attr change, so an implementation must reuse its root rather than
 * create a second one.
 */
export interface WidgetRenderer {
  mount(el: HTMLElement, node: React.ReactNode): void;
  unmount(el: HTMLElement): void;
}

/**
 * React ≤18's legacy pair, read defensively so this file typechecks against BOTH
 * `@types/react-dom@17` (has them) and `@types/react-dom@19` (does not). On React 19 with no
 * host renderer supplied they are `undefined` — the NodeView then shows its alt text, which is
 * the registry's own contract ("a broken widget costs the reader the figure, never the
 * document"), rather than throwing inside ProseMirror's `updateState`.
 */
const legacyDom = ReactDOM as unknown as {
  render?: (node: React.ReactNode, el: Element) => void;
  unmountComponentAtNode?: (el: Element) => boolean;
};

const LEGACY_RENDERER: WidgetRenderer = {
  mount(el, node) {
    if (typeof legacyDom.render !== 'function') {
      throw new Error(
        'This React version removed ReactDOM.render — pass a widgetRenderer (see ./render19).',
      );
    }
    legacyDom.render(node, el);
  },
  unmount(el) {
    legacyDom.unmountComponentAtNode?.(el);
  },
};

/**
 * The registry's contract — "a broken widget must cost the reader the figure, never the
 * document" — has to be enforced HERE too: the NodeView renders synchronously inside
 * ProseMirror's updateState, so an uncaught renderer exception doesn't just lose the figure,
 * it leaves the whole EditorView inconsistent (blank document).
 */
class WidgetErrorBoundary extends React.Component<
  { alt: string; children?: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? (
      <div className="docit-widget-fallback">{this.props.alt || 'Widget unavailable.'}</div>
    ) : (
      this.props.children ?? null
    );
  }
}

class WidgetNodeView {
  readonly dom: HTMLElement;

  constructor(
    private data: WidgetData | null,
    private alt: string,
    private readonly renderer: WidgetRenderer,
  ) {
    this.dom = document.createElement('div');
    this.dom.className = 'docit-widget-nodeview';
    this.dom.contentEditable = 'false';
    this.dom.setAttribute('data-alt', alt);
    this.render();
  }

  private render(): void {
    try {
      this.renderer.mount(
        this.dom,
        <WidgetErrorBoundary alt={this.alt}>
          <WidgetView data={this.data} />
        </WidgetErrorBoundary>,
      );
    } catch {
      // Boundary itself failed — degrade to the alt text rather than poison updateState.
      this.dom.textContent = this.alt || 'Widget unavailable.';
    }
  }

  /** Attr changes (remote peers included) re-render in place. */
  update(node: { type: { name: string }; attrs: { data?: unknown; alt?: string } }): boolean {
    if (node.type.name !== 'widget' && node.type.name !== 'chart') {
      return false;
    }
    this.data = adaptNodeData(node.type.name, node.attrs.data);
    this.alt = String(node.attrs.alt ?? this.alt);
    this.render();
    return true;
  }

  /** The whole subtree is ours; ProseMirror must never re-parse React's DOM. */
  ignoreMutation(): boolean {
    return true;
  }

  /**
   * Without this, every event inside the widget reaches ProseMirror's editHandlers: clicking
   * a sort header or button leaves a NodeSelection on the widget and the NEXT keystroke
   * replaces the whole figure. Drag events pass through (the node is draggable); mousedown
   * passes through only when it lands on non-interactive chrome (so select-to-move still
   * works); everything else belongs to the React tree.
   */
  stopEvent(event: Event): boolean {
    if (event.type.startsWith('drag')) {
      return false;
    }
    const target = event.target as HTMLElement | null;
    const interactive = Boolean(
      target &&
        typeof target.closest === 'function' &&
        target.closest('button, input, select, textarea, a, th, [role="button"]'),
    );
    if (event.type === 'mousedown') {
      return interactive;
    }
    return true;
  }

  destroy(): void {
    try {
      this.renderer.unmount(this.dom);
    } catch {
      // Unmounting must never throw out of ProseMirror's teardown.
    }
  }
}

function adaptNodeData(nodeName: string, raw: unknown): WidgetData | null {
  // Shared coercion + adaptation (doc-widgets): handles Yjs-stringified payloads and the legacy
  // chart kind → type mapping in one place, identical to the v1 lane.
  return nodeName === 'chart'
    ? adaptLegacyChart(raw)
    : (coerceWidgetData(raw) as unknown as WidgetData | null);
}

type NodeArg = { attrs: { data?: unknown; alt?: string } };

/**
 * Factories for editor-core's `extraNodeViews` seam.
 *
 * `renderer` defaults to React ≤18's `ReactDOM.render` pair so existing React-17 hosts call
 * this exactly as before; a React-19 host passes `createReact19WidgetRenderer()`.
 */
export function buildWidgetNodeViews(
  renderer: WidgetRenderer = LEGACY_RENDERER,
): Record<string, (node: unknown, view: unknown, getPos: () => number | undefined) => unknown> {
  const make = (name: 'widget' | 'chart') => (node: unknown) => {
    const n = node as NodeArg;
    return new WidgetNodeView(
      adaptNodeData(name, n.attrs.data),
      (n.attrs.alt as string) ?? '',
      renderer,
    );
  };
  return { widget: make('widget'), chart: make('chart') };
}

/**
 * The v2 insert surface: the IDENTICAL six entries the v1 "+" menu offers, from the same registry
 * with the same sample payloads. Rendered by EditorV2 next to its dock.
 */
export function WidgetInsertBar({
  onInsert,
}: {
  onInsert: (data: Record<string, unknown>, alt: string) => void;
}) {
  return (
    <div
      className="docit-widget-insert-bar"
      style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '4px 0', fontSize: 11 }}
    >
      <span style={{ opacity: 0.6, alignSelf: 'center' }}>Insert widget:</span>
      {(Object.keys(WIDGET_REGISTRY) as WidgetType[]).map((type) => {
        const def = WIDGET_REGISTRY[type];
        return (
          <button
            key={type}
            type="button"
            style={{ fontSize: 11, cursor: 'pointer' }}
            title={def.keywords}
            onClick={() => {
              const data = def.samplePayload();
              onInsert(data as unknown as Record<string, unknown>, deriveAlt(data));
            }}
          >
            {def.label}
          </button>
        );
      })}
    </div>
  );
}
